import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { OWNER_PROPERTIES_PAGE_SIZE, OWNER_PROPERTIES_QUERY_KEY } from "./constants";
import { fetchOwnerFoundingProperties } from "./founding-api";

interface UseOwnerFoundingPropertiesArgs {
  readonly userId: string;
  readonly query: string;
  readonly status: string;
  readonly page: number;
  /**
   * Gates the fetch to viewers allowed to read the owner-properties endpoint
   * (ADMIN/FINANCE), mirroring `useFoundingAccount` — so the hook is safe to
   * mount from any surface, not just the already-gated section it lives in today.
   */
  readonly enabled?: boolean;
}

/**
 * A host's property list — founders and non-founders alike (ADR-0010 §9) —
 * for the per-host assignment surface. `keepPreviousData` keeps the table
 * populated across page/filter changes; the caller resets the selection on
 * those changes so a stale row can never be assigned/unassigned.
 */
export function useOwnerFoundingProperties({
  userId,
  query,
  status,
  page,
  enabled = true,
}: UseOwnerFoundingPropertiesArgs) {
  return useQuery({
    queryKey: [...OWNER_PROPERTIES_QUERY_KEY, userId, query.trim(), status, page] as const,
    queryFn: () =>
      fetchOwnerFoundingProperties(userId, {
        query,
        status,
        page,
        limit: OWNER_PROPERTIES_PAGE_SIZE,
      }),
    enabled: enabled && userId.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}
